import { PLATFORM_MODULES } from "@miplatform/constants";
import { useFeatureFlagsContext } from "@miplatform/feature-flags";
import { MiTHEME } from "@miplatform/theme";
import {
  CalendarEventType,
  IGlobalSignalrMessagePayload,
} from "@miplatform/types";
import { simpleSort } from "@miplatform/utils";
import { useCallback, useEffect, useMemo } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { MiConfigHeaderLogo } from "./components/MiConfigHeaderLogo";
import { useMyCalendarSets, usePermissions } from "./hooks";
import { useGlobalSignalR } from "./hooks/useSignalR/useGlobalSignalR";
import { useGlobalSignalrViewModel } from "./hooks/useSignalR/useGlobalSignalrViewModel";
import { MainLayout } from "./layouts";
import { UnsavedChangesProvider } from "./providers";
import {
  accountingRoutes,
  adminNavItems,
  adminSettingsRoutes,
  dispatchRoutes,
  estimatingRoutes,
  inspectionsRoutes,
  navItems,
  routes,
  schedulingRoutes,
} from "./routes";
import { configNavItems } from "./routes/config";
import { configRoutes } from "./routes/config/configRoutes";
import { legalRoutes } from "./routes/legal";
import { useGlobalSignalrGroupsStore } from "./stores/globalSignalrGroupsStore/useGlobalSignalrGroupsStore";
import {
  generateNavItemsByPermissions,
  generateRoutesByPermissions,
} from "./utils/filterRoutes/filterRoutes";
import { Inspections } from "./views";
import { NhcBoard } from "./views/NewHomeConstruction/NhcBoard";
import { Schedule } from "./views/Schedule/Schedule";

const CALENDAR_SETS = "Calendar Sets";

const WORK_ORDER_CALENDARS = "Work Order Calendars";

const INSPECTIONS = "Inspections";

const ORDER_CALENDARS = "Order Calendars";

export const AuthorizedRoutes = () => {
  const { featureFlags } = useFeatureFlagsContext();
  const { myCalendarSets, getMyCalendarSets } = useMyCalendarSets();
  const { permissions } = usePermissions();
  const { globalSignalrGroups, getGlobalSignalrGroups } =
    useGlobalSignalrViewModel();
  const { setGroupResult, setGroupsState, setConnectionState } =
    useGlobalSignalrGroupsStore();

  useEffect(() => {
    getMyCalendarSets();
    getGlobalSignalrGroups();
  }, []);

  const onGlobalMessageReceived = useCallback(
    (payload: IGlobalSignalrMessagePayload) =>
      setGroupResult(payload.GroupName, payload.Result),
    []
  );

  const { connectionStatus, start, stop } = useGlobalSignalR({ onGlobalMessageReceived });

  useEffect(() => {
    setGroupsState(globalSignalrGroups);
  }, [globalSignalrGroups]);

  useEffect(() => {
    setConnectionState(connectionStatus);
  }, [connectionStatus]);

  useEffect(() => {
    start();
    return () => {
      stop();
    };
  }, [start, stop]);

  const {
    dispatchCalendarNavItems,
    schedulingCalendarNavItems,
    inspectionCalendarNavItems,
  } = useMemo(() => {
    const dispatchCalendarNavItems = myCalendarSets
      .filter(
        (calendarSet) => calendarSet?.module === PLATFORM_MODULES.MI_DISPATCH
      )
      .map((calendarSet, index) => {
        return {
          id: calendarSet.id,
          name: calendarSet.name,
          path: `/dispatch/${calendarSet.id}`,
          testId: `dispatch-nav-item-calendar-option-${index}`,
          element: <Schedule />,
          group: CALENDAR_SETS,
        };
      });

    const schedulingCalendarNavItems = myCalendarSets
      .filter(
        (calendarSet) =>
          calendarSet.module === PLATFORM_MODULES.MI_SCHEDULING &&
          (calendarSet.eventType !== CalendarEventType.WorkOrder ||
            Boolean(featureFlags?.advancedWorkOrdersEnabled))
      )
      .map((calendarSet, index) => {
        return {
          id: calendarSet.id,
          name: calendarSet.name,
          path: `/scheduling/${calendarSet.id}`,
          testId: `scheduling-nav-item-calendar-option-${index}`,
          element: <NhcBoard />,
          group:
            calendarSet.eventType === CalendarEventType.WorkOrder
              ? WORK_ORDER_CALENDARS
              : ORDER_CALENDARS,
        };
      })
      .sort((a, b) => simpleSort(a.group, b.group));

    const inspectionCalendarNavItems = myCalendarSets
      .filter(
        (calendarSet) => calendarSet.module === PLATFORM_MODULES.MI_INSPECTIONS
      )
      .map((calendarSet, index) => {
        return {
          id: calendarSet.id,
          name: calendarSet.name,
          path: `/inspections/${calendarSet.id}`,
          testId: `inspections-nav-item-calendar-option-${index}`,
          element: <Inspections />,
          group: INSPECTIONS,
        };
      });

    return {
      dispatchCalendarNavItems,
      schedulingCalendarNavItems,
      inspectionCalendarNavItems,
    };
  }, [myCalendarSets, featureFlags]);

  const filteredConfigNavItems = useMemo(
    () =>
      configNavItems
        .map((navItem) => {
          const filteredNavItem = {
            ...navItem,
            items: navItem.items?.filter((childNavItem) => {
              if (childNavItem.featureFlagKey) {
                const featureFlagValue =
                  featureFlags[childNavItem.featureFlagKey];

                return featureFlagValue;
              }

              return true;
            }),
          };

          if (filteredNavItem.items?.length === 0) {
            return null;
          }

          if (navItem.featureFlagKey) {
            const featureFlagValue = featureFlags[navItem.featureFlagKey];

            return featureFlagValue ? filteredNavItem : null;
          }

          return filteredNavItem;
        })
        .filter(Boolean),
    [featureFlags]
  );

  const filteredConfigRoutes = useMemo(
    () =>
      configRoutes.filter((route) => {
        if (route.featureFlagKey) {
          const featureFlagValue = featureFlags[route.featureFlagKey];

          return featureFlagValue;
        }

        return true;
      }),
    [featureFlags]
  );

  const mainView = (
    <UnsavedChangesProvider>
      <MainLayout
        sideNavItems={generateNavItemsByPermissions(
          navItems(
            dispatchCalendarNavItems,
            schedulingCalendarNavItems,
            inspectionCalendarNavItems
          ),
          permissions
        )}
        refreshOnNavigate={getMyCalendarSets}
      />
    </UnsavedChangesProvider>
  );

  const settingsView = (
    <MainLayout
      sideNavItems={generateNavItemsByPermissions(adminNavItems, permissions)}
    />
  );
  const configView = (
    <UnsavedChangesProvider>
      <MainLayout
        sideNavItems={generateNavItemsByPermissions(
          filteredConfigNavItems,
          permissions
        )}
        appBarColor={MiTHEME.palette.grey.main}
        appLogo={<MiConfigHeaderLogo />}
        hideSearchBar
      />
    </UnsavedChangesProvider>
  );

  const router = createBrowserRouter([
    {
      path: "/",
      element: mainView,
      children: routes,
    },
    // MiDispatch & Calendar Sets
    {
      path: "",
      element: mainView,
      children: generateRoutesByPermissions(dispatchRoutes, permissions),
    },
    {
      path: "dispatch",
      element: mainView,
      children: generateRoutesByPermissions(
        [
          {
            path: "/dispatch/:id",
            element: <Schedule />,
          },
        ],
        permissions
      ),
    },
    // MiScheduling
    {
      path: "",
      element: mainView,
      children: generateRoutesByPermissions(schedulingRoutes, permissions),
    },
    {
      path: "scheduling",
      element: mainView,
      children: generateRoutesByPermissions(
        [
          {
            path: "/scheduling/:calendarSetId/:calendarId",
            element: <NhcBoard />,
          },
          {
            path: "/scheduling/:calendarSetId",
            element: <NhcBoard />,
          },
        ],
        permissions
      ),
    },
    // MiInspections
    {
      path: "inspections",
      element: mainView,
      children: generateRoutesByPermissions(inspectionsRoutes, permissions),
    },
    // MiAccounting
    {
      path: "accounting",
      element: mainView,
      children: generateRoutesByPermissions(accountingRoutes, permissions),
    },
    // MiEstimating
    {
      path: "estimating",
      element: mainView,
      children: generateRoutesByPermissions(estimatingRoutes, permissions),
    },
    // Settings - Admin
    {
      path: "settings",
      element: settingsView,
      children: generateRoutesByPermissions(adminSettingsRoutes, permissions),
    },
    // Legal
    {
      path: "legal",
      element: mainView,
      children: generateRoutesByPermissions(legalRoutes, permissions),
    },
    // Config
    {
      path: "config",
      element: configView,
      children: generateRoutesByPermissions(filteredConfigRoutes, permissions),
    },
    // Error handling
    {
      path: "*",
      element: (
        <MainLayout
          isError
          sideNavItems={navItems(
            dispatchCalendarNavItems,
            schedulingCalendarNavItems
          )}
        />
      ),
    },
    {
      path: "settings/*",
      element: <MainLayout isError sideNavItems={adminNavItems} />,
    },
    {
      path: "config/*",
      element: (
        <MainLayout
          isError
          sideNavItems={configNavItems}
          appBarColor={MiTHEME.palette.grey.main}
          appLogo={<MiConfigHeaderLogo />}
          hideSearchBar
        />
      ),
    },
  ]);

  return <RouterProvider router={router} />;
};
